import { FC, useState, useEffect } from 'react';
import {
  Card,
  Group,
  Stack,
  Text,
  Button,
  Grid,
} from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { Patient, Encounter } from '@medplum/fhirtypes';
import { usePatientRelatedPersons } from '@hooks/usePatientRelatedPersons';
import { IDENTIFIERS } from '@consts/systems.consts';

interface PatientCardProps {
  patient: Patient;
  onSelect: () => void;
  t: any;
}

/**
 * Patient card component displaying patient summary with selection action
 *
 * @param props - Component props
 * @param props.patient - The patient to display
 * @param props.onSelect - Callback when patient is selected
 * @param props.t - Translation function
 * @returns JSX.Element
 */
export const PatientCard: FC<PatientCardProps> = ({ patient, onSelect, t }) => {
  const medplum = useMedplum();
  const [lastVisit, setLastVisit] = useState<string>('');

  // Use custom hook for fetching related persons (parents)
  const { fatherName, motherName } = usePatientRelatedPersons(patient.id);

  // Fetch last visit on mount
  useEffect(() => {
    if (!patient.id) {
      return;
    }

    medplum
      .searchResources('Encounter', {
        subject: `Patient/${patient.id}`,
        _sort: '-date',
        _count: '1',
      })
      .then((encounters: Encounter[]) => {
        if (encounters.length > 0 && encounters[0].period?.start) {
          setLastVisit(new Date(encounters[0].period.start).toLocaleDateString('el-GR'));
        } else {
          setLastVisit(t('search.neverVisited'));
        }
      })
      .catch(() => {
        setLastVisit(t('search.neverVisited'));
      });
  }, [patient.id, medplum, t]);

  // Parse name components
  const humanName = patient.name?.[0];
  const firstName = humanName?.given?.join(' ') || '-';
  const lastName = humanName?.family || '-';
  const dob = patient.birthDate
    ? new Date(patient.birthDate).toLocaleDateString('el-GR')
    : '-';

  // Extract AMKA from identifiers
  const amkaIdentifier = patient.identifier?.find(
    (id) => id.system === IDENTIFIERS.AMKA
  );
  const amka = amkaIdentifier?.value || '-';

  return (
    <Card withBorder p="md">
      <Group align="flex-start" wrap="nowrap" gap="md">
        <Stack gap="md" style={{ flex: 1 }}>
          <Grid gutter="md">
            {/* First Name */}
            <Grid.Col span={{ base: 12, xs: 6, sm: 4 }}>
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>
                  {t('search.firstName')}
                </Text>
                <Text size="sm" fw={600}>
                  {firstName}
                </Text>
              </Stack>
            </Grid.Col>

            {/* Last Name */}
            <Grid.Col span={{ base: 12, xs: 6, sm: 4 }}>
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>
                  {t('search.lastName')}
                </Text>
                <Text size="sm" fw={600}>
                  {lastName}
                </Text>
              </Stack>
            </Grid.Col>

            {/* Date of Birth */}
            <Grid.Col span={{ base: 12, xs: 6, sm: 4 }}>
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>
                  {t('search.dateOfBirth')}
                </Text>
                <Text size="sm">{dob}</Text>
              </Stack>
            </Grid.Col>

            {/* AMKA */}
            <Grid.Col span={{ base: 12, xs: 6, sm: 4 }}>
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>
                  {t('search.amka')}
                </Text>
                <Text size="sm">{amka}</Text>
              </Stack>
            </Grid.Col>

            {/* Father Name */}
            <Grid.Col span={{ base: 12, xs: 6, sm: 4 }}>
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>
                  {t('search.father')}
                </Text>
                <Text size="sm">{fatherName || '-'}</Text>
              </Stack>
            </Grid.Col>

            {/* Mother Name */}
            <Grid.Col span={{ base: 12, xs: 6, sm: 4 }}>
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>
                  {t('search.mother')}
                </Text>
                <Text size="sm">{motherName || '-'}</Text>
              </Stack>
            </Grid.Col>

            {/* Last Visit */}
            {lastVisit && (
              <Grid.Col span={{ base: 12, xs: 6, sm: 4 }}>
                <Stack gap={4}>
                  <Text size="xs" c="dimmed" fw={500}>
                    {t('search.lastVisit')}
                  </Text>
                  <Text size="sm">{lastVisit}</Text>
                </Stack>
              </Grid.Col>
            )}
          </Grid>
        </Stack>

        <Button size="sm" onClick={onSelect} style={{ flexShrink: 0 }}>
          {t('search.selectPatient')}
        </Button>
      </Group>
    </Card>
  );
};
